import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs, { type Dayjs } from "dayjs";
import { message } from "antd";
import { apiService } from "@/services/api.service";
import { usersService } from "@/services/users.service";
import { handleApiError } from "@/utils/errorHandler";
import { useAttendanceSocket } from "@/hooks/useAttendanceSocket";
import { STAT_CARDS_TEMPLATE } from "@/features/timekeeping/constants";
import type { TimekeepingRecord, TimekeepingStatus } from "@/types/timekeeping";

export type DateRange = [Dayjs, Dayjs] | null;
// Map trạng thái → className CSS sẽ được page sử dụng cho Tag
export type StatusTagClassMap = Record<TimekeepingStatus, string>;

// Convert "HH:mm" (or "--:--") to minutes since 00:00
const timeToMinutes = (timeStr: string): number => {
  if (!timeStr || timeStr === "--:--") return 0;
  const [hours, minutes] = timeStr.split(":").map(Number);
  return hours * 60 + minutes;
};

export interface UseTimekeepingManagementResult {
  // data
  records: TimekeepingRecord[];
  filteredRecords: TimekeepingRecord[];
  paginatedData: TimekeepingRecord[];
  loading: boolean;
  workShiftsMap: Map<
    number,
    {
      checkInTime: string;
      checkOutTime: string;
      lunchBreakStart?: string | null;
      lunchBreakEnd?: string | null;
      standardWorkingHours?: number;
      overtimeStartTime?: string | null;
      overtimeEndTime?: string | null;
    }
  >;

  // filters
  searchText: string;
  setSearchText: (value: string) => void;
  statusFilter: TimekeepingStatus | "all";
  setStatusFilter: (value: TimekeepingStatus | "all") => void;
  selectedDate: DateRange;
  setSelectedDate: (value: DateRange) => void;
  hasActiveFilter: boolean;

  // pagination
  currentPage: number;
  pageSize: number;
  handlePageChange: (page: number, size?: number) => void;
  handlePageSizeChange: (current: number, size: number) => void;

  // stats
  statCards: Array<{
    title: string;
    value: string | number;
    description: string;
    iconSrc: string;
    iconAlt: string;
  }>;

  // modal state
  isModalOpen: boolean;
  selectedRecord: TimekeepingRecord | null;
  openEditModal: (record: TimekeepingRecord) => void;
  closeModal: () => void;

  // actions
  handleApplyFilter: () => void;
  handleClearFilter: () => void;
  handleSubmitTimekeeping: (data: {
    date: Dayjs;
    checkIn?: Dayjs | null;
    checkOut?: Dayjs | null;
    note?: string | null;
  }) => Promise<void>;
  handleDeleteTimekeeping: (record: TimekeepingRecord) => Promise<void>;

  // error
  error: string | null;

  // socket
  isSocketConnected: boolean;
}

interface AttendanceApiRecord {
  id: number;
  userId: number;
  user: {
    id: number;
    fullName: string;
    employeeCode: string;
    email: string;
  } | null;
  type: string;
  status: string;
  method: string;
  date: string;
  time: string;
  note: string | null;
  workShift: {
    id: number;
    name: string;
    code: string;
  } | null;
  createdAt: string;
}

interface AttendanceListResponse {
  success: boolean;
  data: AttendanceApiRecord[];
  pagination?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

const ATTENDANCE_PAGE_LIMIT = 100;

/**
 * Backend giới hạn tối đa 100 bản ghi mỗi trang. Bảng công cần đủ tất cả
 * CHECK_IN/CHECK_OUT trong khoảng ngày trước khi ghép lần vào đầu và lần ra cuối.
 */
const fetchAllAttendances = async (
  fromDate: string,
  toDate: string,
): Promise<AttendanceListResponse> => {
  const recordsById = new Map<number, AttendanceApiRecord>();
  let page = 1;
  let totalPages = 1;
  let lastPagination: AttendanceListResponse["pagination"];

  do {
    const response = await apiService.get<AttendanceListResponse>(
      `/attendance/all?fromDate=${fromDate}&toDate=${toDate}&page=${page}&limit=${ATTENDANCE_PAGE_LIMIT}`,
      true,
    );

    if (!response?.success || !Array.isArray(response.data)) {
      throw new Error("Dữ liệu bảng công không hợp lệ");
    }

    response.data.forEach((attendance) => {
      recordsById.set(attendance.id, attendance);
    });

    lastPagination = response.pagination;

    const reportedTotalPages = Number(response.pagination?.totalPages);

    totalPages =
      Number.isInteger(reportedTotalPages) && reportedTotalPages > 0
        ? reportedTotalPages
        : 1;

    page += 1;
  } while (page <= totalPages);

  return {
    success: true,
    data: Array.from(recordsById.values()),
    pagination: lastPagination
      ? {
          ...lastPagination,
          page: 1,
          limit: ATTENDANCE_PAGE_LIMIT,
          totalPages,
        }
      : undefined,
  };
};

// Helper dùng chung để fetch toàn bộ users (tương tự hook timesheet)
const fetchAllUsers = async (isActive?: boolean) => {
  const allUsers: any[] = [];
  const limit = 200;
  let page = 1;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const res = await usersService.getUsers({ page, limit, isActive });
    if (!res || !res.data) break;

    allUsers.push(...res.data);

    if (res.data.length < limit) break;
    page += 1;
  }

  return allUsers;
};

export const useTimekeepingManagement = (): UseTimekeepingManagementResult => {
  const [records, setRecords] = useState<TimekeepingRecord[]>([]);
  const [filteredRecords, setFilteredRecords] = useState<TimekeepingRecord[]>(
    [],
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [workShiftsMap, setWorkShiftsMap] = useState<
    Map<
      number,
      {
        checkInTime: string;
        checkOutTime: string;
        lunchBreakStart?: string | null;
        lunchBreakEnd?: string | null;
        standardWorkingHours?: number;
        overtimeStartTime?: string | null;
        overtimeEndTime?: string | null;
      }
    >
  >(new Map());
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState<TimekeepingStatus | "all">(
    "all",
  );
  const [selectedDate, setSelectedDate] = useState<DateRange>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] =
    useState<TimekeepingRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fetchRequestIdRef = useRef(0);

  const fromDate = selectedDate
    ? selectedDate[0].format("YYYY-MM-DD")
    : dayjs().startOf("month").format("YYYY-MM-DD");

  const toDate = selectedDate
    ? selectedDate[1].format("YYYY-MM-DD")
    : dayjs().endOf("month").format("YYYY-MM-DD");

  // Tính toán stat cards tổng quan từ filteredRecords
  const statCards = useMemo(() => {
    let needCheckCount = 0;
    let violationMinutes = 0;
    let violationCount = 0;
    let totalOvertimeHours = 0;
    let overtimeCount = 0;
    let fullAttendanceCount = 0;
    let absentCount = 0;

    filteredRecords.forEach((rec) => {
      // Kiểm tra "Cần kiểm tra": những nhân sự chưa có đủ cả check-in và check-out
      // Loại trừ: vắng có lương/không lương (có lý do, không cần kiểm tra)
      const hasCheckIn = rec.checkIn !== "--:--" && rec.checkIn !== "00:00";
      const hasCheckOut = rec.checkOut !== "--:--" && rec.checkOut !== "00:00";
      const hasFullAttendance = hasCheckIn && hasCheckOut;
      const isLeaveWithPay =
        rec.statuses.includes("Vắng có lương") ||
        rec.statuses.includes("Vắng không lương");

      // Tính "Cần kiểm tra" khi:
      // - Chưa có đủ cả check-in và check-out (thiếu một hoặc cả hai)
      // - Và không phải là vắng có lương/không lương
      if (!hasFullAttendance && !isLeaveWithPay) {
        needCheckCount++;
      }

      if (rec.checkIn === "--:--" && rec.checkOut === "--:--") {
        absentCount++;
      }

      if (
        rec.checkIn !== "--:--" &&
        rec.checkOut !== "--:--" &&
        !rec.statuses.includes("Đi trễ") &&
        !rec.statuses.includes("Về sớm")
      ) {
        fullAttendanceCount++;
      }

      if (rec.workShiftId && workShiftsMap.has(rec.workShiftId)) {
        const workShift = workShiftsMap.get(rec.workShiftId)!;
        const checkInMinutes = timeToMinutes(rec.checkIn);
        const checkOutMinutes = timeToMinutes(rec.checkOut);
        const shiftInMinutes = timeToMinutes(workShift.checkInTime);
        const shiftOutMinutes = timeToMinutes(workShift.checkOutTime);

        let hasViolation = false;

        if (
          rec.statuses.includes("Đi trễ") &&
          checkInMinutes > shiftInMinutes
        ) {
          violationMinutes += checkInMinutes - shiftInMinutes;
          hasViolation = true;
        }

        if (
          rec.statuses.includes("Về sớm") &&
          checkOutMinutes < shiftOutMinutes
        ) {
          violationMinutes += shiftOutMinutes - checkOutMinutes;
          hasViolation = true;
        }

        if (hasViolation) {
          violationCount++;
        }
      } else if (
        rec.statuses.includes("Đi trễ") ||
        rec.statuses.includes("Về sớm")
      ) {
        violationCount++;
      }

      if (rec.overtimeHours > 0) {
        totalOvertimeHours += rec.overtimeHours;
        overtimeCount++;
      }
    });

    return STAT_CARDS_TEMPLATE.map((stat) => {
      let value: string | number = stat.value;
      let description = stat.description;

      if (stat.title === "Cần kiểm tra") {
        value = needCheckCount;
        description = "Nhân sự thiếu giờ Ra/Vào";
      } else if (stat.title === "Đi trễ / Về sớm") {
        value = `${violationMinutes} phút`;
        description = `${violationCount} nhân sự vi phạm`;
      } else if (stat.title === "Tăng ca") {
        value = `${Math.round(totalOvertimeHours)}h`;
        description = `${overtimeCount} nhân sự tăng ca`;
      } else if (stat.title === "Đủ công") {
        value = fullAttendanceCount;
        description = `${absentCount} nhân sự vắng mặt`;
      }

      return {
        ...stat,
        value,
        description,
      };
    });
  }, [filteredRecords, workShiftsMap]);

  const handleApplyFilter = useCallback(() => {
    let data = [...records];

    if (searchText.trim()) {
      const term = searchText.toLowerCase().trim();

      data = data.filter(
        (item) =>
          item.employeeName.toLowerCase().includes(term) ||
          item.employeeCode.toLowerCase().includes(term),
      );
    }

    if (statusFilter !== "all") {
      data = data.filter((item) => item.statuses.includes(statusFilter));
    }

    if (selectedDate) {
      const [startDate, endDate] = selectedDate;

      data = data.filter((item) => {
        const itemDate = dayjs(item.dateValue);

        return (
          itemDate.isSame(startDate, "day") ||
          itemDate.isSame(endDate, "day") ||
          (itemDate.isAfter(startDate, "day") &&
            itemDate.isBefore(endDate, "day"))
        );
      });
    }

    setFilteredRecords(data);
    setCurrentPage(1);
  }, [records, searchText, statusFilter, selectedDate]);

  const fetchTimekeepingData = useCallback(async () => {
    const requestId = ++fetchRequestIdRef.current;

    try {
      setLoading(true);

      const [attendanceResponse, usersList] = await Promise.all([
        fetchAllAttendances(fromDate, toDate),
        fetchAllUsers(true),
      ]);

      if (requestId !== fetchRequestIdRef.current) return;

      if (
        !attendanceResponse?.success ||
        !Array.isArray(attendanceResponse.data)
      ) {
        throw new Error("Dữ liệu bảng công không hợp lệ");
      }

      const nonAdminUsers =
        Array.isArray(usersList) && usersList.length
          ? usersList.filter(
              (u: any) => (u.role || "").toString().toLowerCase() !== "admin",
            )
          : [];

      const nonAdminIds = new Set<number>();
      const userAvatarMap = new Map<number, string | null>();
      if (nonAdminUsers.length) {
        nonAdminUsers.forEach((u: any) => {
          nonAdminIds.add(Number(u.id));
          userAvatarMap.set(Number(u.id), (u.avatar || null) as string | null);
        });
      }

      const grouped = new Map<string, TimekeepingRecord>();

      attendanceResponse.data.forEach((att) => {
        if (
          !att.user ||
          (nonAdminIds.size && !nonAdminIds.has(Number(att.userId)))
        )
          return;

        const dateObj = new Date(att.date);
        const dateValue = dayjs(dateObj).format("YYYY-MM-DD");
        const key = `${dateValue}-${att.userId}`;

        let record = grouped.get(key);
        if (!record) {
          const avatar = userAvatarMap.get(att.userId) ?? null;
          record = {
            id: key,
            dateLabel: dayjs(dateObj)
              .locale("vi")
              .format("dddd, D/M/YYYY")
              .replace(/^./, (c) => c.toUpperCase()),
            dateValue,
            employeeName: att.user.fullName,
            employeeCode: att.user.employeeCode || "",
            userId: att.userId,
            avatar,
            checkInId: undefined,
            checkOutId: undefined,
            workShiftId: att.workShift?.id,
            shift: att.workShift?.name || "---",
            checkIn: "--:--",
            checkOut: "--:--",
            coefficient: 1,
            overtimeHours: 0,
            statuses: [],
            methods: [att.method || "GPS"],
            isEdited: att.method === "ADMIN",
            note: att.note || undefined,
          };
        }

        const timeHHmm = att.time?.slice(0, 5) || "--:--";
        if (att.type === "CHECK_IN") {
          if (record.checkIn === "--:--" || timeHHmm < record.checkIn) {
            record = {
              ...record,
              checkIn: timeHHmm,
              checkInId: att.id,
            };
          }
          if (att.note && !record.note) {
            record = {
              ...record,
              note: att.note,
            };
          }
        }
        if (att.type === "CHECK_OUT") {
          if (record.checkOut === "--:--" || timeHHmm > record.checkOut) {
            record = {
              ...record,
              checkOut: timeHHmm,
              checkOutId: att.id,
            };
          }
          if (att.note) {
            record = {
              ...record,
              note: att.note,
            };
          }
        }

        if (att.method && !record.methods.includes(att.method)) {
          record.methods = [...record.methods, att.method];
        }

        if (att.method === "ADMIN") {
          record.isEdited = true;
        }

        grouped.set(key, record);
      });

      const userDefaultShiftMap = new Map<
        number,
        {
          workShiftId?: number;
          shiftName: string;
        }
      >();

      Array.from(grouped.values()).forEach((rec) => {
        if (!rec.userId) return;
        if (!rec.shift || rec.shift === "---") return;

        if (!userDefaultShiftMap.has(rec.userId)) {
          userDefaultShiftMap.set(rec.userId, {
            workShiftId: rec.workShiftId,
            shiftName: rec.shift,
          });
        }
      });

      const workShiftsResponse = await apiService.get<{
        success: boolean;
        data: Array<{
          id: number;
          name: string;
          code: string;
          checkInTime: string;
          checkOutTime: string;
          lunchBreakStart?: string | null;
          lunchBreakEnd?: string | null;
          standardWorkingHours?: number;
          overtimeStartTime?: string | null;
          overtimeEndTime?: string | null;
          priority?: number | null;
          applyToAllEmployees?: boolean;
          applicableUsers?: Array<{ id: number }>;
          applicableDepartments?: Array<{ id: number }>;
        }>;
      }>("/work-shifts?isActive=true&limit=100", true);

      if (requestId !== fetchRequestIdRef.current) return;

      const newWorkShiftsMap = new Map<
        number,
        {
          checkInTime: string;
          checkOutTime: string;
          lunchBreakStart?: string | null;
          lunchBreakEnd?: string | null;
          standardWorkingHours?: number;
          overtimeStartTime?: string | null;
          overtimeEndTime?: string | null;
        }
      >();

      if (
        workShiftsResponse?.success &&
        Array.isArray(workShiftsResponse.data)
      ) {
        workShiftsResponse.data.forEach((shift) => {
          newWorkShiftsMap.set(shift.id, {
            checkInTime: shift.checkInTime,
            checkOutTime: shift.checkOutTime,
            lunchBreakStart: shift.lunchBreakStart,
            lunchBreakEnd: shift.lunchBreakEnd,
            standardWorkingHours: shift.standardWorkingHours,
            overtimeStartTime: shift.overtimeStartTime,
            overtimeEndTime: shift.overtimeEndTime,
          });
        });
      }
      setWorkShiftsMap(newWorkShiftsMap);

      const getApplicableShiftForUser = (user: any) => {
        if (
          !workShiftsResponse?.success ||
          !Array.isArray(workShiftsResponse.data)
        )
          return undefined;

        const userId = Number(user?.id);
        const departmentId =
          user?.departmentId !== undefined && user?.departmentId !== null
            ? Number(user.departmentId)
            : undefined;

        const applicable = workShiftsResponse.data
          .filter((s) => {
            if (s.applyToAllEmployees) return true;
            const inUsers =
              s.applicableUsers?.some((u) => Number(u.id) === userId) ?? false;
            const inDepts =
              departmentId !== undefined &&
              (s.applicableDepartments?.some(
                (d) => Number(d.id) === departmentId,
              ) ??
                false);
            return inUsers || inDepts;
          })
          .sort((a, b) => {
            const pa = (a.priority ?? 100) as number;
            const pb = (b.priority ?? 100) as number;
            if (pa !== pb) return pa - pb;
            return timeToMinutes(a.checkInTime) - timeToMinutes(b.checkInTime);
          });

        return applicable[0];
      };

      if (nonAdminUsers.length) {
        const rangeStart = dayjs(fromDate);
        const rangeEnd = dayjs(toDate);
        const today = dayjs().endOf("day");

        let currentDate = rangeStart;

        while (
          (currentDate.isBefore(rangeEnd, "day") ||
            currentDate.isSame(rangeEnd, "day")) &&
          !currentDate.isAfter(today, "day")
        ) {
          const dateObj = currentDate;
          const dateValue = dateObj.format("YYYY-MM-DD");

          const dateLabel = dateObj
            .locale("vi")
            .format("dddd, D/M/YYYY")
            .replace(/^./, (c) => c.toUpperCase());

          nonAdminUsers.forEach((user: any) => {
            const userId = Number(user.id);
            const key = `${dateValue}-${userId}`;

            if (!grouped.has(key)) {
              const avatar = userAvatarMap.get(userId) ?? null;
              const defaultShift = userDefaultShiftMap.get(userId);
              const applicableShift = getApplicableShiftForUser(user);

              grouped.set(key, {
                id: key,
                dateLabel,
                dateValue,
                employeeName: (user.fullName || user.name || "").toString(),
                employeeCode: (user.employeeCode || "").toString(),
                userId,
                avatar,
                checkInId: undefined,
                checkOutId: undefined,
                workShiftId: defaultShift?.workShiftId ?? applicableShift?.id,
                shift:
                  defaultShift?.shiftName || applicableShift?.name || "---",
                checkIn: "--:--",
                checkOut: "--:--",
                coefficient: 0,
                overtimeHours: 0,
                statuses: ["Vắng mặt"],
                methods: ["GPS"],
                isEdited: false,
                note: undefined,
              } as TimekeepingRecord);
            }
          });

          currentDate = currentDate.add(1, "day");
        }
      }

      const calculateCoefficient = (
        checkIn: string,
        checkOut: string,
        workShiftId?: number,
      ): number => {
        if (checkIn === "--:--" || checkOut === "--:--") return 0;

        const checkInMinutes = timeToMinutes(checkIn);
        const checkOutMinutes = timeToMinutes(checkOut);
        let workingMinutes = Math.max(0, checkOutMinutes - checkInMinutes);

        if (workShiftId) {
          const workShift = newWorkShiftsMap.get(workShiftId);
          if (
            workShift &&
            workShift.lunchBreakStart &&
            workShift.lunchBreakEnd
          ) {
            const lunchStart = timeToMinutes(workShift.lunchBreakStart);
            const lunchEnd = timeToMinutes(workShift.lunchBreakEnd);
            if (checkInMinutes < lunchEnd && checkOutMinutes > lunchStart) {
              const overlap =
                Math.min(checkOutMinutes, lunchEnd) -
                Math.max(checkInMinutes, lunchStart);
              if (overlap > 0) {
                workingMinutes -= overlap;
              }
            }
          }
        }

        if (workingMinutes <= 0) return 0;

        const standardMinutes = 8 * 60;

        if (workingMinutes >= standardMinutes) {
          return 1.0;
        }

        const coefficient = workingMinutes / standardMinutes;
        return Math.round(coefficient * 1000) / 1000;
      };

      // Hàm calculateOvertimeHours đã bị xóa vì giờ làm thêm được nhập thủ công, không tự động tính
      const mappedRecords = Array.from(grouped.values()).map<TimekeepingRecord>(
        (rec) => {
          const statuses: TimekeepingStatus[] = [];
          // Flags để dùng xuyên suốt (đặc biệt cho rule tăng ca)
          let isLate = false;
          let isEarly = false;

          // Kiểm tra note trước để xem có status đặc biệt được lưu không
          // Format: "STATUS:<status_name>" hoặc chứa từ khóa trong note
          let hasSpecialStatus = false;
          let specialStatus: TimekeepingStatus | null = null;

          if (rec.note) {
            const statusMatch = rec.note.match(/STATUS:([^\n]+)/);
            if (statusMatch) {
              const savedStatus = statusMatch[1].trim();
              // Nếu là status đặc biệt, lưu lại để xử lý sau
              if (
                savedStatus === "Vắng có lương" ||
                savedStatus === "Vắng không lương"
              ) {
                hasSpecialStatus = true;
                specialStatus = savedStatus as TimekeepingStatus;
              }
            } else {
              // Kiểm tra từ khóa trong note (backward compatibility)
              if (rec.note.includes("Vắng có lương")) {
                hasSpecialStatus = true;
                specialStatus = "Vắng có lương";
              } else if (rec.note.includes("Vắng không lương")) {
                hasSpecialStatus = true;
                specialStatus = "Vắng không lương";
              }
            }
          }

          // Nếu có status đặc biệt "Vắng có lương" hoặc "Vắng không lương", bỏ qua logic tính toán thông thường
          if (hasSpecialStatus && specialStatus) {
            return {
              ...rec,
              coefficient: 0,
              overtimeHours: 0,
              statuses: [specialStatus],
            };
          }

          const coefficient = calculateCoefficient(
            rec.checkIn,
            rec.checkOut,
            rec.workShiftId,
          );

          // Đọc overtimeHours từ note (được nhập thủ công bởi Admin/HR/CEO)
          // Format: "OVERTIME:3" hoặc "OVERTIME:3.5"
          let overtimeHours = 0;
          if (rec.note) {
            const overtimeMatch = rec.note.match(/OVERTIME:([^\n]+)/);
            if (overtimeMatch) {
              const overtimeValue = parseFloat(overtimeMatch[1].trim());
              if (!isNaN(overtimeValue)) {
                overtimeHours = Math.max(
                  0,
                  Math.round(overtimeValue * 100) / 100,
                );
              }
            }
          }
          // Nếu không có trong note, mặc định là 0 (không tự động tính toán)
          if (
            rec.checkIn !== "--:--" &&
            rec.checkIn !== "00:00" &&
            rec.checkOut !== "--:--" &&
            rec.checkOut !== "00:00"
          ) {
            // Có cả check-in và check-out

            if (rec.workShiftId) {
              const workShift = newWorkShiftsMap.get(rec.workShiftId);
              if (workShift) {
                const checkInMinutes = timeToMinutes(rec.checkIn);
                const checkOutMinutes = timeToMinutes(rec.checkOut);
                const shiftInMinutes = timeToMinutes(workShift.checkInTime);
                const shiftOutMinutes = timeToMinutes(workShift.checkOutTime);

                if (checkInMinutes > shiftInMinutes) {
                  isLate = true;
                  statuses.push("Đi trễ");
                }

                if (checkOutMinutes < shiftOutMinutes) {
                  isEarly = true;
                  statuses.push("Về sớm");
                }
              }
            }

            // Nếu không đi trễ và không về sớm, thêm trạng thái "Đủ công"
            // (Áp dụng cho cả trường hợp có và không có workShiftId)
            if (!isLate && !isEarly) {
              statuses.push("Đủ công");
            }
          } else if (
            rec.checkIn !== "--:--" &&
            rec.checkIn !== "00:00" &&
            rec.checkOut === "--:--"
          ) {
            // Chỉ có check-in, chưa check-out
            let isLate = false;
            if (rec.workShiftId) {
              const workShift = newWorkShiftsMap.get(rec.workShiftId);
              if (workShift) {
                const checkInMinutes = timeToMinutes(rec.checkIn);
                const shiftInMinutes = timeToMinutes(workShift.checkInTime);
                if (checkInMinutes > shiftInMinutes) {
                  isLate = true;
                  statuses.push("Đi trễ");
                }
              }
            }

            if (!isLate) {
              statuses.push("Đã check-in");
            }
          } else if (
            (rec.checkIn === "--:--" || rec.checkIn === "00:00") &&
            (rec.checkOut === "--:--" || rec.checkOut === "00:00")
          ) {
            // Không có check-in và check-out hoặc là thời gian 00:00 (vắng)
            statuses.push("Vắng mặt");
          }

          // Rule: Không được tính/hiển thị tăng ca nếu nhân sự về sớm (check-out trước giờ ra ca)
          // Lý do: tránh trạng thái mâu thuẫn "Về sớm" + "Tăng ca" (như ảnh report).
          if (isEarly && overtimeHours > 0) {
            overtimeHours = 0;
          }

          // Xử lý trạng thái "Tăng ca" dựa trên giờ làm thêm
          // 1. Nếu có giờ làm thêm (overtimeHours > 0), tự động thêm "Tăng ca"
          // 2. Nếu không có giờ làm thêm (overtimeHours = 0), xóa "Tăng ca" (trừ khi được set thủ công trong note)
          // Kiểm tra xem có "Tăng ca" được set thủ công trong note không
          let hasManualOvertimeStatus = false;
          if (rec.note) {
            const statusMatch = rec.note.match(/STATUS:([^\n]+)/);
            if (statusMatch) {
              const savedStatus = statusMatch[1].trim();
              if (
                savedStatus === "Tăng ca" ||
                savedStatus.includes("Tăng ca")
              ) {
                hasManualOvertimeStatus = true;
              }
            }
          }

          // Nếu về sớm thì không cho giữ "Tăng ca" dù set thủ công
          if (isEarly) {
            hasManualOvertimeStatus = false;
          }

          if (overtimeHours > 0) {
            // Nếu có giờ làm thêm, tự động thêm "Tăng ca"
            if (!statuses.includes("Tăng ca")) {
              statuses.push("Tăng ca");
            }
            // Nếu đang có "Đủ công" và có "Tăng ca", xóa "Đủ công" (chỉ hiển thị "Tăng ca")
            if (statuses.includes("Đủ công") && statuses.includes("Tăng ca")) {
              const index = statuses.indexOf("Đủ công");
              statuses.splice(index, 1);
            }
            // Nếu có "Đi trễ" và "Tăng ca", giữ cả hai (hiển thị "Đi trễ, Tăng ca")
          } else {
            // Nếu không có giờ làm thêm, xóa "Tăng ca" khỏi status (trừ khi được set thủ công)
            if (!hasManualOvertimeStatus) {
              const overtimeIndex = statuses.indexOf("Tăng ca");
              if (overtimeIndex !== -1) {
                statuses.splice(overtimeIndex, 1);
              }
            } else {
              // Nếu được set thủ công trong note, vẫn giữ "Tăng ca"
              if (!statuses.includes("Tăng ca")) {
                statuses.push("Tăng ca");
              }
            }
          }

          // Đảm bảo luôn có ít nhất một trạng thái
          const finalStatuses =
            statuses.length > 0
              ? statuses
              : rec.checkIn === "--:--" && rec.checkOut === "--:--"
                ? ["Vắng mặt"]
                : [];

          return {
            ...rec,
            coefficient,
            overtimeHours,
            statuses: finalStatuses as TimekeepingStatus[],
          };
        },
      );

      mappedRecords.sort((a, b) => {
        const dateComparison = b.dateValue.localeCompare(a.dateValue);
        return dateComparison || a.employeeName.localeCompare(b.employeeName, "vi");
      });

      setRecords(mappedRecords);
      setError(null);
    } catch (err: any) {
      if (requestId !== fetchRequestIdRef.current) return;
      const errorMessage = handleApiError(
        err,
        "Không thể tải dữ liệu bảng công",
      );
      setError(
        typeof errorMessage === "string"
          ? errorMessage
          : "Không thể tải dữ liệu bảng công",
      );
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    fetchTimekeepingData();
  }, [fetchTimekeepingData]);

  useEffect(() => {
    handleApplyFilter();
  }, [handleApplyFilter]);

  const handleClearFilter = () => {
    setSearchText("");
    setStatusFilter("all");
    setSelectedDate(null);
    setFilteredRecords(records);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number, size?: number) => {
    setCurrentPage(page);
    if (size) {
      setPageSize(size);
    }
  };

  const handlePageSizeChange = (current: number, size: number) => {
    setCurrentPage(current);
    setPageSize(size);
  };

  const openEditModal = (record: TimekeepingRecord) => {
    setSelectedRecord(record);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedRecord(null);
  };

  const handleSubmitTimekeeping = async (data: {
    date: Dayjs;
    checkIn?: Dayjs | null;
    checkOut?: Dayjs | null;
    note?: string | null;
    status?: TimekeepingStatus;
    overtimeHours?: number;
  }) => {
    try {
      if (!selectedRecord || !selectedRecord.userId) {
        throw new Error("Không tìm thấy thông tin nhân viên");
      }

      const dateStr = data.date.format("YYYY-MM-DD");
      const userId = selectedRecord.userId;
      const selectedWorkShiftId = selectedRecord.workShiftId;

      const attendanceResponse = await apiService.get<{
        success: boolean;
        date: string;
        attendances: Array<{
          id: number;
          userId: number;
          type: string;
          time: string;
          workShift: { id: number; name: string; code: string } | null;
        }>;
      }>(`/attendance/by-date?date=${dateStr}&userId=${userId}`, true);

      if (
        !attendanceResponse?.success ||
        !Array.isArray(attendanceResponse.attendances)
      ) {
        throw new Error("Không thể lấy dữ liệu chấm công");
      }

      const checkInRecord = attendanceResponse.attendances
        .filter((att) => att.type === "CHECK_IN")
        .sort((a, b) => a.time.localeCompare(b.time))[0];

      const checkOutRecord = attendanceResponse.attendances
        .filter((att) => att.type === "CHECK_OUT")
        .sort((a, b) => b.time.localeCompare(a.time))[0];

      let workShiftId: number | undefined = undefined;
      if (checkInRecord?.workShift) {
        workShiftId = checkInRecord.workShift.id;
      } else if (checkOutRecord?.workShift) {
        workShiftId = checkOutRecord.workShift.id;
      }

      // Xử lý note và status một cách linh hoạt
      let finalNote = data.note || "";
      const hasCheckIn = data.checkIn !== null && data.checkIn !== undefined;
      const hasCheckOut = data.checkOut !== null && data.checkOut !== undefined;
      const hasActualTime = hasCheckIn || hasCheckOut;

      // Loại bỏ các metadata cũ (STATUS, OVERTIME) khỏi note trước khi thêm mới
      finalNote = finalNote.replace(/STATUS:[^\n]+\n?/g, "");
      finalNote = finalNote.replace(/OVERTIME:[^\n]+\n?/g, "");
      finalNote = finalNote.trim();

      // Nếu có check-in/check-out thực tế, tự động xóa status đặc biệt "Vắng có lương"/"Vắng không lương" khỏi note
      if (hasActualTime) {
        // Đã loại bỏ ở trên, không cần làm gì thêm
      }

      // Xử lý status: chỉ lưu status đặc biệt vào note nếu không có thời gian thực tế
      if (data.status) {
        const statusToSave = data.status;

        // Các status đặc biệt cần lưu vào note
        if (
          statusToSave === "Vắng có lương" ||
          statusToSave === "Vắng không lương"
        ) {
          // Chỉ lưu status này nếu KHÔNG có check-in/check-out thực tế
          if (!hasActualTime) {
            const statusPattern = /STATUS:[^\n]+/;
            if (statusPattern.test(finalNote)) {
              // Thay thế status cũ
              finalNote = finalNote.replace(
                statusPattern,
                `STATUS:${statusToSave}`,
              );
            } else {
              // Thêm status mới
              if (finalNote.trim()) {
                finalNote = `STATUS:${statusToSave}\n${finalNote}`;
              } else {
                finalNote = `STATUS:${statusToSave}`;
              }
            }
          }
          // Nếu có thời gian thực tế, không lưu status này (đã xóa ở trên)
        }
        // Lưu ý: "Tăng ca" không được lưu vào note, vì nó được tự động tính dựa trên overtimeHours
        // Nếu overtimeHours > 0, "Tăng ca" sẽ tự động được thêm vào status khi load dữ liệu
      }

      // Lưu overtimeHours vào note
      // Nếu overtimeHours > 0: lưu vào note
      // Nếu overtimeHours = 0 hoặc null/undefined: xóa OVERTIME khỏi note (đã xóa ở trên)
      const overtimeValue = data.overtimeHours ?? 0; // Mặc định là 0 nếu null/undefined

      // Rule: Không cho lưu tăng ca nếu check-out trước giờ ra ca (về sớm)
      // Điều này giúp tránh dữ liệu mâu thuẫn và cũng khớp với rule khi load dữ liệu.
      if (overtimeValue > 0 && hasCheckOut && data.checkOut) {
        const effectiveShiftId = selectedWorkShiftId ?? workShiftId;
        if (effectiveShiftId) {
          const ws = workShiftsMap.get(effectiveShiftId);
          if (ws?.checkOutTime) {
            const checkOutMinutes = timeToMinutes(
              data.checkOut.format("HH:mm"),
            );
            const shiftOutMinutes = timeToMinutes(ws.checkOutTime);
            if (checkOutMinutes < shiftOutMinutes) {
              message.error(
                "Không thể tăng ca khi nhân sự về sớm (check-out trước giờ ra ca). Vui lòng chỉnh lại giờ ra hoặc OT.",
              );
              return;
            }
          }
        }
      }

      if (overtimeValue > 0) {
        // Thêm OVERTIME vào đầu note
        if (finalNote.trim()) {
          finalNote = `OVERTIME:${overtimeValue}\n${finalNote}`;
        } else {
          finalNote = `OVERTIME:${overtimeValue}`;
        }
      }
      // Nếu overtimeHours = 0, không lưu OVERTIME vào note (đã xóa ở trên)
      // Xử lý check-in
      if (hasCheckIn && data.checkIn) {
        const checkInTime = data.checkIn.format("HH:mm:ss");
        if (checkInRecord) {
          // Cập nhật bản ghi check-in hiện có
          await apiService.put<{ success: boolean }>(
            `/attendance/${checkInRecord.id}`,
            {
              time: checkInTime,
              date: dateStr,
              ...(workShiftId ? { workShiftId } : {}),
              ...(selectedWorkShiftId
                ? { workShiftId: selectedWorkShiftId }
                : {}),
              note: finalNote,
            },
            true,
          );
        } else {
          // Tạo bản ghi check-in mới
          await apiService.post<{ success: boolean }>(
            `/attendance/manual`,
            {
              userId,
              type: "CHECK_IN",
              date: dateStr,
              time: checkInTime,
              ...(selectedWorkShiftId
                ? { workShiftId: selectedWorkShiftId }
                : workShiftId
                  ? { workShiftId }
                  : {}),
              note: finalNote,
            },
            true,
          );
        }
      }

      // Xử lý check-out
      if (hasCheckOut && data.checkOut) {
        const checkOutTime = data.checkOut.format("HH:mm:ss");
        if (checkOutRecord) {
          // Cập nhật bản ghi check-out hiện có
          await apiService.put<{ success: boolean }>(
            `/attendance/${checkOutRecord.id}`,
            {
              time: checkOutTime,
              date: dateStr,
              ...(workShiftId ? { workShiftId } : {}),
              ...(selectedWorkShiftId
                ? { workShiftId: selectedWorkShiftId }
                : {}),
              note: finalNote,
            },
            true,
          );
        } else {
          // Tạo bản ghi check-out mới
          await apiService.post<{ success: boolean }>(
            `/attendance/manual`,
            {
              userId,
              type: "CHECK_OUT",
              date: dateStr,
              time: checkOutTime,
              ...(selectedWorkShiftId
                ? { workShiftId: selectedWorkShiftId }
                : workShiftId
                  ? { workShiftId }
                  : {}),
              note: finalNote,
            },
            true,
          );
        }
      }

      // Nếu chọn "Vắng có lương" hoặc "Vắng không lương" và không có check-in/check-out thực tế
      // Cập nhật hoặc tạo bản ghi đánh dấu
      if (
        (data.status === "Vắng có lương" ||
          data.status === "Vắng không lương") &&
        !hasActualTime
      ) {
        // Nếu đã có bản ghi check-in với time 00:00:00, chỉ cập nhật note
        if (checkInRecord && checkInRecord.time === "00:00:00") {
          await apiService.put<{ success: boolean }>(
            `/attendance/${checkInRecord.id}`,
            {
              date: dateStr,
              note: finalNote,
            },
            true,
          );
        } else if (!checkInRecord && !checkOutRecord) {
          // Nếu chưa có bản ghi nào, tạo một bản ghi CHECK_IN với time 00:00:00 để đánh dấu là vắng
          await apiService.post<{ success: boolean }>(
            `/attendance/manual`,
            {
              userId,
              type: "CHECK_IN",
              date: dateStr,
              time: "00:00:00",
              ...(selectedWorkShiftId
                ? { workShiftId: selectedWorkShiftId }
                : workShiftId
                  ? { workShiftId }
                  : {}),
              note: finalNote,
            },
            true,
          );
        } else if (checkInRecord && checkInRecord.time !== "00:00:00") {
          // Nếu có bản ghi check-in với thời gian thực tế, cập nhật thành 00:00:00 và note
          await apiService.put<{ success: boolean }>(
            `/attendance/${checkInRecord.id}`,
            {
              time: "00:00:00",
              date: dateStr,
              note: finalNote,
            },
            true,
          );
          // Xóa check-out nếu có
          if (checkOutRecord) {
            try {
              await apiService.delete<{ success: boolean }>(
                `/attendance/${checkOutRecord.id}`,
                true,
              );
            } catch (err) {
              // Ignore lỗi nếu không xóa được
            }
          }
        }
      }

      await fetchTimekeepingData();

      message.success("Cập nhật chấm công thành công");
      closeModal();
    } catch (err: any) {
      handleApiError(err, "Có lỗi xảy ra khi cập nhật chấm công");
      throw err;
    }
  };

  const handleDeleteTimekeeping = async (record: TimekeepingRecord) => {
    try {
      if (!record.userId || !record.dateValue) {
        throw new Error("Không tìm thấy thông tin nhân sự hoặc ngày chấm công");
      }

      await apiService.delete<{
        success: boolean;
        deletedCount: number;
      }>(
        `/attendance/by-date?date=${record.dateValue}&userId=${record.userId}`,
        true,
      );

      await fetchTimekeepingData();

      message.success("Đã xóa dữ liệu chấm công của nhân sự trong ngày này");
    } catch (err) {
      handleApiError(err, "Có lỗi xảy ra khi xóa bản ghi chấm công");
    }
  };

  const paginatedData = useMemo(
    () =>
      filteredRecords.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize,
      ),
    [filteredRecords, currentPage, pageSize],
  );

  const handleCheckIn = useCallback(() => {
    fetchTimekeepingData().catch(() => {});
  }, [fetchTimekeepingData]);

  const handleCheckOut = useCallback(() => {
    fetchTimekeepingData().catch(() => {});
  }, [fetchTimekeepingData]);

  const handleUpdate = useCallback(() => {
    fetchTimekeepingData().catch(() => {});
  }, [fetchTimekeepingData]);

  const { isConnected } = useAttendanceSocket({
    onCheckIn: handleCheckIn,
    onCheckOut: handleCheckOut,
    onUpdate: handleUpdate,
  });

  const hasActiveFilter = Boolean(
    searchText.trim() ||
    (statusFilter && statusFilter !== "all") ||
    selectedDate,
  );

  return {
    records,
    filteredRecords,
    paginatedData,
    loading,
    workShiftsMap,
    searchText,
    setSearchText,
    statusFilter,
    setStatusFilter,
    selectedDate,
    setSelectedDate,
    hasActiveFilter,
    currentPage,
    pageSize,
    handlePageChange,
    handlePageSizeChange,
    statCards,
    isModalOpen,
    selectedRecord,
    openEditModal,
    closeModal,
    handleApplyFilter,
    handleClearFilter,
    handleSubmitTimekeeping,
    handleDeleteTimekeeping,
    error,
    isSocketConnected: isConnected,
  };
};
